import type { TFunction } from "i18next";
import type { WebuildAiPlatform } from "@webuild-ai/agent-platform";
import type { UploadedFile } from "@/stores/ChatStore";

import { requireChatId } from "./requireChatId";
import { type UploadParser, uploadAndAwaitParse } from "./uploadAndAwaitParse";

/**
 * Uploads one file attached in a chat composer (the main chat or the Workflow
 * Builder's build chat) and waits for it to be parsed.
 *
 * This is the one place the chat id contract is enforced: the id is checked with
 * `requireChatId` before anything is sent, then passed unchanged, and the upload
 * is always private to the user.
 *
 * @param platformClient - The agent platform client.
 * @param file - The file to upload.
 * @param chatId - The chat the upload belongs to.
 * @param t - The translation function, for the generic failure message.
 * @param parser - Parser the platform runs over the file, `"llm"` unless OCR is wanted.
 * @returns The uploaded file, with the job result as its id.
 * @throws When `chatId` is missing, empty or only whitespace, before anything is
 * uploaded, and otherwise as `uploadAndAwaitParse` does.
 */
export const uploadChatFile = async (
  platformClient: WebuildAiPlatform | null | undefined,
  file: File,
  chatId: string | null | undefined,
  t: TFunction,
  parser: UploadParser = "llm",
): Promise<UploadedFile> => {
  const requiredChatId = requireChatId(chatId);

  return uploadAndAwaitParse(
    platformClient,
    file,
    { chatId: requiredChatId, isPrivate: true, parser },
    t,
  );
};
